import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const RevenueAnalytics = ({ data, loading }) => {
  const phaseLabels = {
    planning: 'Planning',
    permits: 'Permits',
    foundation: 'Foundation',
    framing: 'Framing',
    systems: 'Systems',
    finishing: 'Finishing',
    completed: 'Completed'
  };

  const chartData = data?.map(item => ({
    phase: phaseLabels?.[item?.phase] || item?.phase,
    projected: item?.projected,
    actual: item?.actual
  })) || [];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const projected = payload?.find(p => p?.dataKey === 'projected');
      const actual = payload?.find(p => p?.dataKey === 'actual');
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-semibold text-foreground mb-2">{payload?.[0]?.payload?.phase}</p>
          {projected && (
            <p className="text-xs text-primary mb-1">
              Projected: ${(projected?.value ?? 0)?.toLocaleString()}
            </p>
          )}
          {actual && (
            <p className="text-xs text-success">
              Actual: ${(actual?.value ?? 0)?.toLocaleString()}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-center h-80">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="DollarSign" size={20} className="text-success" />
            Revenue Pipeline
          </h3>
          <p className="text-xs text-muted-foreground mt-1">Projected vs actual revenue by phase</p>
        </div>
      </div>
      {chartData?.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-80 text-muted-foreground">
          <Icon name="TrendingUp" size={48} className="mb-2 opacity-50" />
          <p className="text-sm">No revenue data available</p>
        </div>
      ) : (
        <div className="w-full h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="phase" 
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 11 }}
                axisLine={{ stroke: 'var(--color-border)' }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                axisLine={{ stroke: 'var(--color-border)' }}
                tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px', color: 'var(--color-muted-foreground)' }}
              />
              <Bar 
                dataKey="projected" 
                fill="#1B3A5C" 
                radius={[4, 4, 0, 0]}
                name="Projected"
              />
              <Bar 
                dataKey="actual" 
                fill="#E8732A" 
                radius={[4, 4, 0, 0]}
                name="Actual"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default RevenueAnalytics;